import React, { useEffect, useState, useRef } from 'react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { Button } from '../ui/button';

interface BorderedSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  disabled?: boolean;
  options: Array<{ value: string; label: string }>;
  label?: string;
  error?: string;
  className?: string;
  textClassName?: string;
  searchEnabled?: boolean;
  /** Override default max width of the trigger (e.g. "300px"). Default 250px. */
  containerMaxWidth?: string;
  /** Text when no value selected. Default "Select an option". Use "" or "None" for empty/none. */
  placeholder?: string;
}

const BorderedSelect: React.FC<BorderedSelectProps> = ({
  disabled = false,
  options,
  label,
  error,
  className,
  textClassName,
  searchEnabled = false,
  containerMaxWidth = '250px',
  placeholder = 'Select an option',
  ...selectProps
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchQuery) {
      setFilteredOptions(options.filter((opt) => opt.label && opt.label.toLowerCase().includes(searchQuery.toLowerCase())));
    } else {
      setFilteredOptions(options);
    }
  }, [searchQuery, options]);

  // Keep input focused when dropdown is open and search is enabled
  useEffect(() => {
    if (open && searchEnabled && searchInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [open, searchEnabled]);

  // Maintain focus when filtered options change (prevents focus loss during typing)
  useEffect(() => {
    if (open && searchEnabled && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      // Only refocus if we're not already focused (to avoid interrupting typing)
      requestAnimationFrame(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }
  }, [filteredOptions, open, searchEnabled]);


  return (
    <div className="flex flex-col">
      <div
        className={`group relative rounded cursor-pointer ${error ? 'border-red-500' : ''} ${className}`}
        style={{
          paddingLeft: '1px',
          paddingRight: '5px',
          display: 'inline-block',
          maxWidth: containerMaxWidth,
          maxHeight: 36,
        }}
      >
        <DropdownMenu
          open={open}
          onOpenChange={setOpen}
        >
          {label && (
            <label className="absolute -top-2 left-3 mb-1 text-xs font-light text-gray-500">
              {label}
            </label>
          )}
          <DropdownMenuTrigger asChild>

            <Button
              disabled={disabled}
              className={`appearance-none bg-transparent w-full px-3 py-2 rounded transition-all duration-300
                border-1 border-transparent 
                group-hover:border-gray-500 
                group-hover:border 
                //group-focus-within:border-1 
                //group-focus-within:border-gray-500
                //focus:outline-none
                ${disabled ? '!cursor-not-allowed' : 'cursor-pointer'}
                ${error ? 'group-hover:border-red-500 group-focus-within:border-red-500' : ''}
                `}
              variant="ghost"
              size="med"
              onClick={() => setOpen(!open)}
            >

              <span className={`${textClassName || 'text-gray-500'} ${disabled ? '!cursor-not-allowed' : 'cursor-pointer'}`}>
                {selectProps.value ? options.find((opt) => opt.value === selectProps.value)?.label : placeholder}
              </span>
              {/* <ChevronDown className="size-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" /> */}
            </Button>

          </DropdownMenuTrigger>
          <DropdownMenuContent
            onCloseAutoFocus={(e) => {
              // Prevent focus from returning to trigger when closing
              if (searchEnabled) {
                e.preventDefault();
              }
            }}
          >
            {searchEnabled && (
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search"
                className="w-full px-3 py-2 border-b border-gray-400 text-sm text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Keep focus on input
                  e.target.focus();
                }}
                onKeyDown={(e) => {
                  // Prevent dropdown from closing on Escape when typing
                  if (e.key === 'Escape' && searchQuery) {
                    e.stopPropagation();
                    setSearchQuery('');
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              />
            )}
            {filteredOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => {
                  selectProps.onChange?.({ target: { value: opt.value } } as unknown as React.ChangeEvent<HTMLSelectElement>);
                  setOpen(false);
                }}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default BorderedSelect;
